import { useClasseContext } from "../../context/ClasseContext";
import EvalNatLoader from "../EvalNatLoader";
import MultiSelect from "../../components/general/MultiSelect";
import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { useEvalNat } from "../../hooks/fetch_api_hook/useEvalNat";
import ModalImportEvalNatChef from "../../components/VueChef/ModalImportEvalNatChef";
import { classesAvecCopies } from "../../utils/campagneEvalNat";
import { importNationalesOuvert, MENTION_IMPORT_NATIONALES_A_VENIR } from "../../utils/ouvertureImportNationales";
import { BarChart3, Users, Building2, Sparkles, TrendingUp, Info, AlertCircle, CheckCircle2, Upload } from "lucide-react";

function DashBoardChef(){
    const { niveauEvalnat, setNiveauEvalnat} = useClasseContext();
      const { etablissementData, user, refreshEtablissementData } = useUser();
  const professeur = etablissementData?.professeurs?.find(p => p.id === user?.id);
  const isDelegue = !!professeur?.delegue_affectation;
  // Le chef importe toujours ; le délégué selon le drapeau ; un autre professeur jamais
  // (cette page se joint aussi par son adresse).
  const importOuvert = importNationalesOuvert(user, isDelegue);

  const [initialListClasses, setInitialListClasses] = useState() // Garde la liste initiale
  const [selectedClasse, setSelectedClasse] = useState()
  const [isSchool, setIsSchool] = useState(false)
  const [classeWithoutResults, setClasseWithoutResults] = useState()

  const [schoolWitouthResults, setSchoolWithouthResults] = useState();
  const [selectedMatiere, setSelectedMatiere] = useState('maths'); // Par défaut: maths
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer les évaluations nationales de l'établissement
  const { data: EvalsNatGroupe, isLoading: isLoadingEvalsNat } = useEvalNat(
    etablissementData?.id ? `?etablissement_id=${parseInt(etablissementData.id, 10)}${isDelegue ? '&delegue_mode=true' : ''}` : null,
    null,
    false
  );

  // Vérifier si les données nécessaires sont chargées
  const isDataLoaded = !isLoadingEvalsNat && etablissementData?.classes && EvalsNatGroupe;

  // Calculer les classes avec résultats par niveau et matière
  const getClassesStatus = (niveau, matiere) => {
    if (!etablissementData?.classes || !EvalsNatGroupe) {
      return { withResults: [], withoutResults: [] };
    }

    const classesOfNiveau = etablissementData.classes.filter(c => c.nom?.[0] === niveau);
    // Évaluation par classe ou campagne du niveau (D9) ; un bilan ne compte pas (défaut 12).
    const classesWithResults = classesAvecCopies(EvalsNatGroupe, { niveau, matiere });

    return {
      withResults: classesOfNiveau.filter(c => classesWithResults.has(c.nom)).map(c => c.nom),
      withoutResults: classesOfNiveau.filter(c => !classesWithResults.has(c.nom)).map(c => c.nom)
    };
  };

  // Calculer toutes les classes manquantes (tous niveaux, toutes matières)
  const getAllMissingClasses = useMemo(() => {
    if (!etablissementData?.classes || !EvalsNatGroupe) {
      return { total: 0, byNiveauMatiere: {} };
    }

    const niveaux = ['6', '5', '4'];
    const matieres = ['maths', 'français'];
    const byNiveauMatiere = {};
    let total = 0;

    niveaux.forEach(niveau => {
      matieres.forEach(matiere => {
        const status = getClassesStatus(niveau, matiere);
        if (status.withoutResults.length > 0) {
          const key = `${niveau}ème ${matiere === 'maths' ? 'Maths' : 'Français'}`;
          byNiveauMatiere[key] = {
            niveau,
            matiere,
            classes: status.withoutResults
          };
          total += status.withoutResults.length;
        }
      });
    });

    return { total, byNiveauMatiere };
  }, [etablissementData?.classes, EvalsNatGroupe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculer listClasses avec useMemo 
  const listClasses = useMemo(() => {
    if (!initialListClasses) return initialListClasses;

    // On récupère le filtre.
    const filterList = isSchool ? schoolWitouthResults : classeWithoutResults;

    if (filterList && filterList.length >= 0) {
      return initialListClasses.filter((item) => !filterList.includes(item.nom));
    }

    return initialListClasses;
  }, [initialListClasses, classeWithoutResults, schoolWitouthResults, isSchool]);

  // Mettre à jour selectedClasse quand listClasses change
  useEffect(() => {
    if (!listClasses || !selectedClasse) return;

    const newSelected = selectedClasse.filter((option) =>
      listClasses.some((item) => item.id === option.value)
    );

    // Comparer les IDs pour voir si c'est vraiment différent
    const currentIds = selectedClasse.map(s => s.value).sort().join(',');
    const newIds = newSelected.map(s => s.value).sort().join(',');

    if (currentIds !== newIds) {
      setSelectedClasse(newSelected);
    }
  }, [listClasses, selectedClasse]); // Inclure selectedClasse mais avec vérification pour éviter la boucle

  // Utiliser useEffect pour réinitialiser l'état lors de la navigation
  useEffect(() => {
    // Fonction de nettoyage à chaque changement de route
    return () => {
        setNiveauEvalnat('');
        setInitialListClasses([]);
        setSelectedClasse([]);
        setIsSchool(false);
        setClasseWithoutResults([]);
    };
  }, [location, setNiveauEvalnat]); // Chaque fois que l'URL change

const handleNiveauSelected = (item) => {
  let filteredClasses = '' ;
  if(item ==="ECOLE"){
    setIsSchool(true)
    filteredClasses = etablissementData.ecoles
  }
  else {
    filteredClasses = etablissementData.classes.filter(
      (c) => c.nom[0] === item
    );
  }

  setNiveauEvalnat(item);
  setInitialListClasses(filteredClasses); // Sauvegarder la liste initiale (listClasses sera calculé par useMemo)

  // Mettre à jour selectedClasse avec toutes les classes filtrées
  const selectedOptions = filteredClasses.map((item) => ({
    label: item.nom,
    value: item.id,
  }));
  setSelectedClasse(selectedOptions);
}

const handleClasseChange = (selectedOptions) => {
  setSelectedClasse(selectedOptions)
}

const handleBack = () => {
  setNiveauEvalnat('');
  setIsSchool(false);
  setClasseWithoutResults('')
}

const SectionButton = ({ title, description, filterBy, isEcole, icon: Icon, gradient }) => {
 let filteredItems = ''
 if(etablissementData){
   filteredItems = isEcole
    ? etablissementData.ecoles
    : etablissementData.classes.filter((c) => c.nom[0] === filterBy);
 }

  const itemCount = filteredItems ? filteredItems.length : 0;

  // Calculer le statut par matière (seulement pour les classes, pas les écoles)
  const mathsStatus = !isEcole ? getClassesStatus(filterBy, 'maths') : null;
  const francaisStatus = !isEcole ? getClassesStatus(filterBy, 'français') : null;
  return (
    <div className={`group relative bg-white rounded-2xl shadow-lg transition-all duration-300 overflow-hidden transform ${
      isDataLoaded
        ? 'hover:shadow-2xl cursor-pointer hover:-translate-y-2'
        : 'opacity-90 cursor-not-allowed'
    }`}
         onClick={() => isDataLoaded && handleNiveauSelected(isEcole ? "ECOLE" : filterBy)}>

      {/* Gradient animé en arrière-plan */}
      <div className={`absolute inset-0 ${gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>

      {/* Motifs décoratifs */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-transparent rounded-full -translate-y-16 translate-x-16 opacity-50"></div>

      <div className="relative p-6 xl:p-4 2xl:p-5 3xl:p-6 flex flex-col h-full">
        {/* Icône et badge compteur */}
        <div className="flex items-start justify-between mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
          <div className={`p-3 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-xl ${gradient} bg-opacity-10 group-hover:scale-110 transition-transform duration-300`}>
            <Icon className={`w-8 h-8 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 ${gradient.includes('blue') ? 'text-blue-600' : gradient.includes('purple') ? 'text-purple-600' : gradient.includes('emerald') ? 'text-emerald-600' : 'text-cyan-600'}`} />
          </div>

          <div className="flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 bg-gray-100 px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 rounded-full">
            <TrendingUp className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-3.5 2xl:h-3.5 3xl:w-4 3xl:h-4 text-gray-600" />
            <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold text-gray-700">{itemCount} {isEcole ? 'écoles' : 'classes'}</span>
          </div>
        </div>

        {/* Titre */}
        <h3 className="text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-bold text-gray-800 mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3 group-hover:text-blue-600 transition-colors">
          {title}
        </h3>

        {/* Description */}
        <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-600 mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4 leading-relaxed">
          {description}
        </p>

        {/* Statut par matière (seulement pour les classes) */}
        {!isEcole && (
          <div className="space-y-3 xl:space-y-2 2xl:space-y-2.5 3xl:space-y-3 mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
            {/* Mathématiques */}
            <div className="bg-gray-50 rounded-lg p-3 xl:p-2 2xl:p-2.5 3xl:p-3">
              <div className="flex items-center justify-between mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold text-gray-700 flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                  📐 Mathématiques
                </span>
              </div>
              {!isDataLoaded ? (
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs">Chargement...</span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {mathsStatus?.withResults.map((classe, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {classe}
                    </span>
                  ))}
                  {mathsStatus?.withoutResults.map((classe, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {classe}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Français */}
            <div className="bg-gray-50 rounded-lg p-3 xl:p-2 2xl:p-2.5 3xl:p-3">
              <div className="flex items-center justify-between mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold text-gray-700 flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                  📖 Français
                </span>
              </div>
              {!isDataLoaded ? (
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs">Chargement...</span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {francaisStatus?.withResults.map((classe, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {classe}
                    </span>
                  ))}
                  {francaisStatus?.withoutResults.map((classe, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {classe}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pour les écoles, afficher simplement la liste */}
        {isEcole && (
          <div className="bg-gray-50 rounded-lg p-3 xl:p-2 2xl:p-2.5 3xl:p-3 mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
            <p className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-gray-500 mb-1 font-medium">Écoles disponibles :</p>
            <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-700 font-medium">
              {filteredItems && filteredItems.map((item) => item.nom).join(", ").slice(0, 80)}
              {filteredItems && filteredItems.map((item) => item.nom).join(", ").length > 80 && '...'}
            </p>
          </div>
        )}

        {/* Bouton d'action */}
        {!isDataLoaded ? (
          <div className="flex items-center justify-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 px-4 xl:px-3 2xl:px-3.5 3xl:px-4 rounded-xl bg-gray-300 text-gray-500 font-semibold xl:text-sm 2xl:text-sm 3xl:text-base mt-auto cursor-not-allowed">
            <div className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            <span>Chargement...</span>
          </div>
        ) : (
          <div className={`flex items-center justify-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 px-4 xl:px-3 2xl:px-3.5 3xl:px-4 rounded-xl ${gradient} bg-opacity-90 text-white font-semibold xl:text-sm 2xl:text-sm 3xl:text-base group-hover:bg-opacity-100 transition-all mt-auto`}>
            <BarChart3 className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
            <span>Consulter les résultats</span>
          </div>
        )}
      </div>
    </div>
  );
};


return  (
  <div className=" px-4 pt-4 xl:px-3 xl:pt-3 2xl:px-3.5 2xl:pt-3.5 3xl:px-4 3xl:pt-4 flex flex-col w-full h-full">
      {/* Header principal avec gradient animé */}
      <div className="relative mb-8 xl:mb-5 2xl:mb-6 3xl:mb-8">
        {/* Gradient de fond animé */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-600 opacity-90 transition-all duration-1000 rounded-2xl"></div>

        {/* Motifs décoratifs */}
        <div className="absolute inset-0 opacity-10 rounded-2xl overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-32 -translate-y-32"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full translate-x-48 -translate-y-48"></div>
          <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-white rounded-full translate-y-40"></div>
        </div>

        {/* Contenu du header */}
        <div className="relative z-10 px-6 xl:px-4 2xl:px-5 3xl:px-6 py-8 xl:py-5 2xl:py-6 3xl:py-8">
          <div className="w-full">
            {/* Titre et description */}
            <div className="mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6 text-white">
              <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
                <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-white/20 backdrop-blur-md rounded-xl">
                  <BarChart3 className="w-8 h-8 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 text-white" />
                </div>
                <h1 className="xl:text-xl 2xl:text-2xl 3xl:text-3xl text-2xl font-bold tracking-tight flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  Tableau de Bord des Évaluations Nationales
                  <Sparkles className="w-7 h-7 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-yellow-300 animate-pulse" />
                </h1>
              </div>

              <p className="xl:text-sm 2xl:text-base 3xl:text-xl text-lg text-white/90 font-light ml-16 xl:ml-11 2xl:ml-13 3xl:ml-16">
                {niveauEvalnat
                  ? `Résultats détaillés - ${isSchool ? 'Par école de secteur' : `Classes de ${niveauEvalnat}ème`}`
                  : 'Consultez les résultats des évaluations nationales par niveau ou par école'
                }
              </p>

              {/* Bouton global d'import, toujours là pour qui importe (plan du 30/10/2026, choix 4) : sans classe
                  manquante, il sert à réimporter pour compléter, quand des élèves manquaient dans une classe importée. */}
              {!niveauEvalnat && (
                isDataLoaded ? (
                  importOuvert ? (
                    <button
                      onClick={() => setIsImportModalOpen(true)}
                      className={`mt-4 xl:mt-3 2xl:mt-3.5 3xl:mt-4 ml-16 xl:ml-11 2xl:ml-13 3xl:ml-16 flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 xl:py-2 2xl:py-2 3xl:py-2.5 text-white font-semibold xl:text-sm 2xl:text-sm 3xl:text-base rounded-xl transition-all ${getAllMissingClasses.total > 0 ? 'bg-orange-500 hover:bg-orange-600 shadow-lg hover:shadow-xl' : 'bg-white/20 hover:bg-white/30 border border-white/30'}`}
                    >
                      <Upload className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                      {getAllMissingClasses.total > 0
                        ? `Importer les résultats manquants (${getAllMissingClasses.total} résultat(s))`
                        : 'Réimporter pour compléter'}
                    </button>
                  ) : getAllMissingClasses.total > 0 ? (
                    <span
                      className="mt-4 xl:mt-3 2xl:mt-3.5 3xl:mt-4 ml-16 xl:ml-11 2xl:ml-13 3xl:ml-16 inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 border border-white/30 text-white/90 font-semibold xl:text-sm 2xl:text-sm 3xl:text-base rounded-xl"
                      title="L'import des résultats nationaux par le professeur délégué n'est pas encore ouvert."
                    >
                      <Upload className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                      Import des résultats · {MENTION_IMPORT_NATIONALES_A_VENIR}
                    </span>
                  ) : null
                ) : (
                  <div className="mt-4 xl:mt-3 2xl:mt-3.5 3xl:mt-4 ml-16 xl:ml-11 2xl:ml-13 3xl:ml-16 flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 xl:py-2 2xl:py-2 3xl:py-2.5 bg-white/20 text-white font-medium xl:text-sm 2xl:text-sm 3xl:text-base rounded-xl">
                    <div className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Chargement des résultats...
                  </div>
                )
              )}
            </div>

            {/* Alertes et messages */}
            {isSchool && (
              <div className="mt-5 xl:mt-3 2xl:mt-4 3xl:mt-5 p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl">
                <div className="flex items-start gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <Info className="w-6 h-6 xl:w-5 xl:h-5 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white flex-shrink-0 mt-1" />
                  <div className="text-white">
                    <p className="xl:text-sm 2xl:text-base 3xl:text-lg lg:text-base text-sm font-semibold mb-1">
                      Astuce importante
                    </p>
                    <p className="xl:text-xs 2xl:text-sm 3xl:text-base text-sm text-white/90">
                      Vérifiez dans le menu <strong>"École de rattachement"</strong> si tous les élèves de 6ème sont affectés à une école.
                      Cela garantit des résultats complets et pertinents pour les résultats par école de secteur.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {niveauEvalnat && (
              <>
                {classeWithoutResults && classeWithoutResults.length > 0 ? (
                  <div className="mt-5 xl:mt-3 2xl:mt-4 3xl:mt-5 p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-gradient-to-r from-red-500/50 to-orange-500/50 backdrop-blur-md border-2 border-red-400 rounded-xl shadow-xl">
                    <div className="flex items-start gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                      <AlertCircle className="w-6 h-6 xl:w-5 xl:h-5 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white flex-shrink-0 mt-1 animate-pulse" />
                      <div className="text-white flex-1">
                        <p className="xl:text-sm 2xl:text-base 3xl:text-lg lg:text-base text-sm font-bold mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                          ⚠️ Classes sans résultats d'évaluations nationales en {selectedMatiere}
                        </p>
                        <div className="flex flex-wrap gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
                          {classeWithoutResults.map((e, idx) => (
                            <span key={idx} className="inline-flex items-center px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 bg-white/20 backdrop-blur rounded-full text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium">
                              {e}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="xl:text-xs 2xl:text-sm 3xl:text-base text-sm text-white/95 font-medium">
                            {importOuvert ? "Utilisez le bouton d'import global pour importer les résultats." : `Import des résultats : ${MENTION_IMPORT_NATIONALES_A_VENIR.toLowerCase()}.`}
                          </p>
                          {importOuvert && <button
                            onClick={() => setIsImportModalOpen(true)}
                            className="flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 bg-white text-orange-600 hover:bg-orange-50 font-semibold xl:text-sm 2xl:text-sm 3xl:text-base rounded-lg transition-colors shadow-lg"
                          >
                            <Upload className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-3.5 2xl:h-3.5 3xl:w-4 3xl:h-4" />
                            Importer les résultats
                          </button>}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 xl:mt-3 2xl:mt-4 3xl:mt-5 p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-green-500/30 backdrop-blur-md border border-green-400/70 rounded-xl shadow-lg">
                    <div className="flex items-start gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                      <CheckCircle2 className="w-6 h-6 xl:w-5 xl:h-5 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-green-100 flex-shrink-0 mt-1" />
                      <p className="xl:text-sm 2xl:text-base 3xl:text-lg lg:text-base text-sm font-semibold text-white">
                        Les résultats des évaluations nationales en {selectedMatiere} pour toutes les classes de ce niveau sont complets et disponibles !
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {!isSchool && !niveauEvalnat && (
              <p className="text-white/90 xl:text-xs 2xl:text-sm 3xl:text-lg lg:text-base text-sm mt-2 ml-16 xl:ml-11 2xl:ml-13 3xl:ml-16">
                Cliquez sur les cartes ci-dessous pour explorer les résultats des évaluations nationales par classe ou école.
                Vous pouvez ensuite affiner votre sélection pour afficher les données qui vous intéressent.
              </p>
            )}
          </div>
        </div>
      </div>
    {!niveauEvalnat && (
      <div className="w-full grid xl:grid-cols-2 grid-cols-1 gap-6 xl:gap-4 2xl:gap-5 3xl:gap-6">
        {/* Classes de 6ème */}
        <SectionButton
          title="Classes de 6ème"
          description="Visualisez les résultats des évaluations nationales pour les classes de 6ème. Analysez les performances par classe et identifiez les besoins."
          filterBy="6"
          isEcole={false}
          icon={Users}
          gradient="bg-gradient-to-r from-blue-500 to-blue-600"
        />
        {/* Classes de 5ème */}
        <SectionButton
          title="Classes de 5ème"
          description="Visualisez les résultats des évaluations nationales pour les classes de 5ème. Suivez la progression et identifiez les axes d'amélioration."
          filterBy="5"
          isEcole={false}
          icon={Users}
          gradient="bg-gradient-to-r from-purple-500 to-purple-600"
        />
        {/* Classes de 4ème */}
        <SectionButton
          title="Classes de 4ème"
          description="Visualisez les résultats des évaluations nationales pour les classes de 4ème. Comparez les performances et adaptez votre pédagogie."
          filterBy="4"
          isEcole={false}
          icon={Users}
          gradient="bg-gradient-to-r from-emerald-500 to-emerald-600"
        />
        {/* Écoles de secteur */}
        <SectionButton
          title="Par école de secteur"
          description="Analysez les résultats des évaluations nationales pour les élèves de 6ème regroupés par école de secteur. Vue d'ensemble par origine géographique."
          filterBy="6"
          isEcole={true}
          icon={Building2}
          gradient="bg-gradient-to-r from-cyan-500 to-cyan-600"
        />
      </div>
    )}



{niveauEvalnat &&
        <div className=" flex flex-col w-full">
          <p  onClick={handleBack} className=" button p-2 bg-orange-500 w-fit rounded text-white hover:bg-orange-600 hover:cursor-pointer"> ← Retour </p>

          {/* Conteneur pour Sélection classes et Filtre matière */}
          <div className="bg-white text-lg xl:text-sm 2xl:text-base 3xl:text-lg rounded-3xl p-4 xl:p-3 2xl:p-3.5 3xl:p-4 m-5 xl:m-3 2xl:m-4 3xl:m-5 w-fit">
            <div className="flex flex-col lg:flex-row gap-6 xl:gap-4 2xl:gap-5 3xl:gap-6 items-start lg:items-center">
              {/* Sélection des classes */}
              <div className="flex flex-col gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                <span className="text-gray-700 font-medium xl:text-sm 2xl:text-sm 3xl:text-base">
                {niveauEvalnat !== 'ECOLE'
                    ? `Sélectionner les classes de ${niveauEvalnat}èmes :`
                    : "Sélectionner les écoles de secteur"
                  }
                  </span>
                <MultiSelect
                  onChange={handleClasseChange}
                  labels={listClasses}
                  value={selectedClasse}
                  showSelectAll={true}
                />
              </div>

              {/* Séparateur vertical sur desktop */}
              <div className="hidden lg:block w-px h-12 bg-gray-300"></div>

              {/* Filtre matière */}
              <div className="flex flex-col gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                <span className="text-gray-700 font-medium xl:text-sm 2xl:text-sm 3xl:text-base">Matière :</span>
                <div className="flex bg-white bg-opacity-20 rounded-full p-1 border border-gray-200">
                  <button
                    onClick={() => setSelectedMatiere('maths')}
                    className={`px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-full text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold transition-all ${
                      selectedMatiere === 'maths'
                        ? 'bg-gradient-to-r from-sky-600 to-sky-400 text-white shadow-md'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    📐 Mathématiques
                  </button>
                  <button
                    onClick={() => setSelectedMatiere('français')}
                    className={`px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-full text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold transition-all ${
                      selectedMatiere === 'français'
                        ? 'bg-gradient-to-r from-sky-600 to-sky-400 text-white shadow-md'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    📖 Français
                  </button>
                </div>
              </div>
            </div>
          </div>

          <EvalNatLoader 
          typeComponent={"nationale"} selectedClasse={selectedClasse} bySchool={isSchool} selectedMatiere={selectedMatiere} niveauEvalnat={niveauEvalnat} setClasseWithoutResults={setClasseWithoutResults}
          setSchoolWithouthResults ={setSchoolWithouthResults} evalsNatGroupeFromParent={EvalsNatGroupe} />
        </div>
}

      {/* Modal d'import des évaluations nationales */}
      <ModalImportEvalNatChef
        isOpen={isImportModalOpen && importOuvert}
        onClose={() => setIsImportModalOpen(false)}
        allMissingClasses={getAllMissingClasses}
        etablissementData={etablissementData}
        onRelireClasses={refreshEtablissementData}
      />
     </div>
  


      
)
    
}
export default DashBoardChef;